import React, { useState, useRef } from 'react';
import { IoMdMegaphone } from 'react-icons/io';
import {
  MdAssessment,
  MdDashboard,
  MdEmail,
  MdLogin,
  MdLogout,
  MdMap,
} from 'react-icons/md';
import { FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'hooks/useClickOutside';
import { Disclaimer, Modal } from 'components';
import logoGov from 'assets/svg/logo-gov.svg';
import logoSGB from 'assets/img/logo-sgb.png';
import logoSGBEnUS from 'assets/img/logo-sgb-en-us_1.png';
import logoP3M from 'assets/img/logo-title.png';
import logoSmallP3M from 'assets/img/logo-title-small.png';
import { FlagSwitch } from './components/FlagSwitch';
import GoogleTranslate from './components/TranslateComponent';
import links from './links';
import * as Styled from './styles';
import DropdownGov from './components/DropdownGov';
import env from '../../../package.json';
import { useAuth } from 'contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { finalizeMonitoringSession } from 'services/monitoring';

export function Header() {
  const { value: disclaimerStatus } = JSON.parse(sessionStorage.getItem('disclaimerStatus') || '{}');
  const { isAdmin, user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMonitoringPage = pathname === '/monitoramento';
  const isDashboardPage = pathname === '/dashboard';
  const isMapPage = pathname === '/map';

  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState<boolean>(!disclaimerStatus ?? true);
  const [isContactModalOpen, setIsContactModalOpen] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setDropdownOpen(false));

  const { t: translation, i18n } = useTranslation();

  const handleOnAccept = () => {
    setIsDisclaimerOpen(false);
    sessionStorage.setItem('disclaimerStatus', JSON.stringify({ value: true }));
  };

  const handleOnCheck = (value: boolean) => {
    sessionStorage.setItem('disclaimerStatus', JSON.stringify({ value }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Styled.Wrapper>
      <nav className="govBar">
        <img src={logoGov} alt="gov.br" />
        <div className="links">
          {links.map(item => (
            <a key={item.id} className="link" href={item.link} target="_blank" rel="noreferrer">
              {translation(`header.${item.label}`)}
            </a>
          ))}
        </div>
        <div className="dropdownLinks">
          <DropdownGov label="Gov links" links={links} />
        </div>
      </nav>
      <Styled.Header className="nav">
        <a href="https://www.sgb.gov.br/" target="_blank" rel="noreferrer">
          {i18n.language === 'en-US' ? (
            <img src={logoSGBEnUS} alt="SGB" className="sgbLogoEnUs" />
          ) : (
            <img src={logoSGB} alt="SGB" className="sgbLogo" />
          )}
        </a>
        <div className="versionLogo nav">
          <img src={logoP3M} alt="P3M" className="P3MlogoBlock" />
          <img src={logoSmallP3M} alt="P3M" className="P3MlogoSmallBlock" />
          <p>v. {env.version}</p>
        </div>
        <div className="linksBlock">
          <button id="contactButton" type="button" onClick={() => setIsContactModalOpen(true)}>
            <IoMdMegaphone size={24} />
            <div id="contactLabel">{translation('header.contact')}</div>
          </button>
          { <FlagSwitch /> }
          <Styled.UserDropdownContainer ref={dropdownRef}>
            <Styled.UserDropdownToggle
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-label={user ? `Menu do usuário: ${user.name}` : "Menu de acesso anônimo"}
            >
              {user ? (
                user.picture ? (
                  <Styled.UserAvatar
                    src={user.picture}
                    alt={user.name}
                    title={user.name}
                    referrerPolicy="no-referrer"
                    onError={e => {
                      const img = e.currentTarget;
                      img.style.display = 'none';
                      const fallback = img.nextElementSibling as HTMLElement | null;
                      if (fallback) fallback.style.display = '';
                    }}
                  />
                ) : null
              ) : null}
              {user ? (
                user.picture ? (
                  <Styled.UserInitials title={user.name} style={{ display: 'none' }}>
                    {user.name.charAt(0).toUpperCase()}
                  </Styled.UserInitials>
                ) : (
                  <Styled.UserInitials title={user.name}>
                    {user.name.charAt(0).toUpperCase()}
                  </Styled.UserInitials>
                )
              ) : (
                <Styled.AnonymousAvatar title="Acesso Público">
                  <FaUser />
                </Styled.AnonymousAvatar>
              )}
            </Styled.UserDropdownToggle>
            {dropdownOpen && (
              <Styled.UserDropdownMenu>
                {user ? (
                  <>
                    <Styled.UserMenuHeader>
                      <div className="user-name">{user.name}</div>
                      <div className="user-email">{user.email}</div>
                    </Styled.UserMenuHeader>
                    <Styled.UserMenuDivider />
                      <Styled.UserMenuItem
                        type="button"
                        disabled={isMapPage}
                        aria-current={isMapPage ? 'page' : undefined}
                        onClick={() => {
                          setDropdownOpen(false);
                          navigate('/map');
                        }}
                      >
                        <MdMap size={18} />
                        <span>Início</span>
                      </Styled.UserMenuItem>
                      <Styled.UserMenuItem
                        type="button"
                        disabled={isDashboardPage}
                        aria-current={isDashboardPage ? 'page' : undefined}
                        onClick={() => {
                          setDropdownOpen(false);
                          navigate('/dashboard');
                        }}
                      >
                        <MdDashboard size={18} />
                        <span>Dashboard</span>
                      </Styled.UserMenuItem>
                    {isAdmin && (
                      <>
                        <Styled.UserMenuItem
                          type="button"
                          disabled={isMonitoringPage}
                          aria-current={isMonitoringPage ? 'page' : undefined}
                          onClick={() => {
                            setDropdownOpen(false);
                            navigate('/monitoramento');
                          }}
                        >
                          <MdAssessment size={18} />
                          <span>Monitoramento</span>
                        </Styled.UserMenuItem>
                      </>
                    )}
                    <Styled.UserMenuDivider />
                    <Styled.UserMenuItem
                      type="button"
                      onClick={async () => {
                        setDropdownOpen(false);
                        await handleLogout();
                      }}
                    >
                      <MdLogout size={18} />
                      <span>Sair</span>
                    </Styled.UserMenuItem>
                  </>
                ) : (
                  <>
                    <Styled.UserMenuHeader>
                      <div className="user-name">Acesso Anônimo</div>
                    </Styled.UserMenuHeader>
                    <Styled.UserMenuDivider />
                      <Styled.UserMenuItem
                        type="button"
                        disabled={isMapPage}
                        aria-current={isMapPage ? 'page' : undefined}
                        onClick={() => {
                          setDropdownOpen(false);
                          navigate('/map');
                        }}
                      >
                        <MdMap size={18} />
                        <span>Início</span>
                      </Styled.UserMenuItem>
                      <Styled.UserMenuItem
                        type="button"
                        disabled={isDashboardPage}
                        aria-current={isDashboardPage ? 'page' : undefined}
                        onClick={() => {
                          setDropdownOpen(false);
                          navigate('/dashboard');
                        }}
                      >
                        <MdDashboard size={18} />
                        <span>Dashboard</span>
                      </Styled.UserMenuItem>
                    <Styled.UserMenuDivider />
                    <Styled.UserMenuItem
                      type="button"
                      onClick={async () => {
                        setDropdownOpen(false);
                        await finalizeMonitoringSession({ useBeacon: true });
                        navigate('/login');
                      }}
                    >
                      <MdLogin size={18} />
                      <span>Entrar</span>
                    </Styled.UserMenuItem>
                  </>
                )}
              </Styled.UserDropdownMenu>
            )}
          </Styled.UserDropdownContainer>
        </div>
        <GoogleTranslate/>
      </Styled.Header>
      {isDisclaimerOpen && (
        <Disclaimer title={translation('disclaimer.title')} onAccept={handleOnAccept} onCheck={handleOnCheck} />
      )}
      {isContactModalOpen && (
        <Modal onDisabled={() => setIsContactModalOpen(false)}>
          <h2>{translation('header.contact')}</h2>
          <Styled.ContentEmail>
            <span>Email: </span>
            <div className="contentLine">
              <MdEmail size={24} />
              <a href="mailto:p3m@sgb.gov.br" target="_blank" rel="noreferrer">
                p3m@sgb.gov.br
              </a>
            </div>
          </Styled.ContentEmail>
        </Modal>
      )}
    </Styled.Wrapper>
  );
}
